import React, { ReactNode } from 'react';
import * as css from './textInput.module.scss';
import classNames from 'classnames';

interface HideBorder {
  hideBorder?: boolean;
  highlighted?: never;
}

interface Highlighted {
  highlighted?: boolean;
  hideBorder?: never;
}

export type Border = HideBorder | Highlighted;

export interface TextInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  htmlId?: string;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  helpText?: ReactNode;
  helpTextRole?: string;
  onChange: (
    value: string,
    event?: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  dataCy?: string;
  children?: ReactNode;
}

interface TextInputPropsWithAriaLabel extends TextInputProps {
  ariaLabel: string;
  label?: never;
}

interface TextInputPropsWithLabel extends TextInputProps {
  label: string;
  ariaLabel?: never;
}

type Props = (TextInputPropsWithLabel | TextInputPropsWithAriaLabel) & Border;

export const TextInput: React.FC<React.PropsWithChildren<Props>> = ({
  htmlId,
  containerClassName,
  labelClassName,
  inputClassName,
  disabled,
  onChange,
  required,
  ariaLabel,
  label,
  hideBorder,
  highlighted,
  inputRef,
  helpText,
  helpTextRole,
  type,
  onFocus,
  dataCy,
  children,
  ...props
}) => {
  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value, event);
  };

  const onFocusHandler = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.select();
    if (onFocus) onFocus(event);
  };

  return (
    <div
      className={classNames(css.textInputContainer, containerClassName || '')}
    >
      <span
        className={classNames(
          css.textInputLabel,
          labelClassName || '',
          disabled ? css.disabled : '',
        )}
      >
        {label && (
          <label htmlFor={htmlId}>
            {label} {required && <span className={css.required}>*</span>}
          </label>
        )}
        <input
          id={htmlId}
          tabIndex={0}
          className={classNames(
            css.textInput,
            inputClassName || '',
            hideBorder ? css.hideBorder : '',
            highlighted ? css.highlighted : '',
          )}
          data-cy={dataCy ? dataCy : 'text-input'}
          onFocus={onFocusHandler}
          aria-label={ariaLabel}
          ref={inputRef}
          disabled={disabled}
          required={required}
          onChange={onChangeHandler}
          type={type || 'text'}
          {...props}
        />
        {children}
      </span>
      {helpText && <span role={helpTextRole}>{helpText}</span>}
    </div>
  );
};
